import React, { useState, useContext, useEffect } from 'react';
import { Card, Row, Col, NavDropdown, Form  } from 'react-bootstrap';
import axios from 'axios'; 
import { Button, TextField, MenuItem } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useWidgetContext } from '../context/DragContext';
import { AuthContext } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import SwitchAccount from '../common/SwitchAccount';
import { showSweetAlert } from './SweetAlert';
import PreviewOrderPopup from './PerviewOrderPopup';

function Trade() {
    const [amount, setAmount] = useState('');
    const [strikePrice, setStrikePrice] = useState('');
    const [selectedButton, setSelectedButton] = useState('Shares');
    const [selectedBuySellButton, setSelectedBuySellButton] = useState('BUY');
    const [selectedCallPutButton, setSelectedCallPutButton] = useState('CALL');
    const [fromAccount, setFromAccount] = useState('');
    const [timeInForce, setTimeInForce] = useState('Day');
    const { removeWidgetFromGrid } = useWidgetContext();
    const { currentDate, currentTime, createOrder, getOrderData, userData, thirdParyAccount, symbolData, baseCurencySymbol, curencySymbol, symbol_type, cryptocomSymbolValid, cryptocomSymbolValidationMessage } = useContext(AuthContext);
    const [verified, setVerified] = useState(true);
    const [switchAccount, setSwitchAccount] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isDemoPath = location.pathname === '/demo';
    const token = localStorage.getItem('accessToken');
    const [showPreview, setShowPreview] = useState(false);
    const [orderParams, setOrderParams] = useState(null);
    const [preOrderConfirmation, setPreOrderConfirmation] = useState(false);
    const [confirmationRequired, setConfirmationRequired] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmationId, setConfirmationId] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const handleOpenSwitchAccount = () => setSwitchAccount(true);
    const handleCloseSwitchAccount = () => setSwitchAccount(false);

    const handleOpenPreview = () =>{
        
        if (isSubmitDisabled) {
            // Show error if required fields are missing
            showSweetAlert(
                'Error!',
                'Please enter the amount and select an order type before previewing the order.',
                'error'
            );
            return;
        }

        // Validate limit price if order type is Limit
        if (fromAccount === 'LMT' && (!strikePrice || strikePrice === 0 || strikePrice === '')) {
            showSweetAlert(
                'Error!',
                'Please enter a limit price for Limit orders.',
                'error'
            );
            return;
        }

        let newOrderParams;
        if(thirdParyAccount === 'IBKR'){
            newOrderParams = {
                quantity: selectedButton === 'Shares' ? amount : "",
                side: selectedBuySellButton,
                cashqty: selectedButton === 'Dollars' ? amount : "",
                ordertype: fromAccount
            };
        }
        else if(thirdParyAccount === 'etrade'){
            if(symbol_type === 'otc'){
                newOrderParams = {
                    quantity: amount,
                    side: selectedBuySellButton === 'BUY' ? 'BUY_OPEN' : (selectedBuySellButton === 'SELL' ? 'SELL_CLOSE' : ''),
                    qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                    ordertype: fromAccount,
                    callput: selectedCallPutButton,
                    strikePrice: strikePrice
                };
            }else if(symbol_type === 'stocks' || symbol_type === 'indices'){
                newOrderParams = {
                    quantity: amount,
                    side: selectedBuySellButton,
                    qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                    ordertype: fromAccount,
                    strikePrice: strikePrice,
                    ...(fromAccount === 'LMT' ? { timeInForce: timeInForce } : {})
                };
            } else {
                // Default for other symbol types with etrade
                newOrderParams = {
                    quantity: amount,
                    side: selectedBuySellButton,
                    qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                    ordertype: fromAccount,
                    strikePrice: strikePrice
                };
            }
        }
        else {
            // Default case for other providers
            newOrderParams = {
                quantity: amount,
                side: selectedBuySellButton,
                qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                ordertype: fromAccount,
                strikePrice: strikePrice
            };
        }

        // Only show preview if orderParams is set
        if (newOrderParams) {
            setOrderParams(newOrderParams);
            setShowPreview(true);
        }
    }
    const handleClosePreview = (success) => {
        setShowPreview(false);
        
        if (success) {
            setVerified(false);
            setAmount('');
        }
    };
    

    const handleAmountChange = (event) => {
        const value = parseFloat(event.target.value, 10);
        setAmount(isNaN(value) ? "" : value);
    };
    const handleStrikePriceChange = (event) => {
        const value = parseFloat(event.target.value, 10);
        setStrikePrice(isNaN(value) ? "" : value);
    };
    const handleButtonClick = (buttonName) => {
        setSelectedButton(buttonName);
    };
    const handleBuySellButtonClick = (buttonName) => {
        setSelectedBuySellButton(buttonName);
    };
    const handleCallPutButtonClick = (buttonName) => {
        setSelectedCallPutButton(buttonName);
    };
    const handleFromAccountChange = (event) => {
        setFromAccount(event.target.value);
    };
    const handleTimeInForceChange = (event) => {
        setTimeInForce(event.target.value);
    };

    const [showDropdown, setShowDropdown] = useState(false);

    const handleToggle = (isOpen) => {
        setShowDropdown(isOpen);
    };

    const handleSvgClick = () => {
        setShowDropdown(!showDropdown);
    };

    // Determine if the submit button should be disabled
    // For Crypto.com, also check if symbol is valid
    const isSymbolInvalid = thirdParyAccount === 'cryptocom' && !cryptocomSymbolValid;
    const isSubmitDisabled = !amount || !selectedButton || !selectedBuySellButton || !fromAccount || isSymbolInvalid;

    const handleSubmit = async () => {
        if (isSubmitDisabled) {
            return;
        }

        // Validate limit price if order type is Limit
        if (fromAccount === 'LMT' && (!strikePrice || strikePrice === 0 || strikePrice === '')) {
            showSweetAlert(
                'Error!',
                'Please enter a limit price for Limit orders.',
                'error'
            );
            return;
        }
        
        // For IBKR, show pre-order confirmation first
        if(thirdParyAccount === 'IBKR'){
            const ibkrOrderParams = {
                quantity: selectedButton === 'Shares' ? amount : "",
                side: selectedBuySellButton,
                cashqty: selectedButton === 'Dollars' ? amount : "",
                ordertype: fromAccount
            };
            setOrderParams(ibkrOrderParams);
            setPreOrderConfirmation(true);
            return;
        }
        
        let orderParams;
        if(thirdParyAccount === 'etrade'){
            if(symbol_type === 'otc'){
                orderParams = {
                    quantity: amount,
                    side: selectedBuySellButton === 'BUY' ? 'BUY_OPEN' : (selectedBuySellButton === 'SELL' ? 'SELL_CLOSE' : ''),
                    qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                    ordertype: fromAccount,
                    ...(fromAccount === 'LMT' || fromAccount === 'TRAILLMT' ? { limitPrice: strikePrice } : {})
                };
            }else if(symbol_type === 'stocks' || symbol_type === 'indices'){
                orderParams = {
                    quantity: amount,
                    side: selectedBuySellButton,
                    qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR',
                    ordertype: fromAccount,
                    ...(fromAccount === 'LMT' || fromAccount === 'TRAILLMT' ? { limitPrice: strikePrice } : {})
                };
            }
        } else if(thirdParyAccount === 'cryptocom' || thirdParyAccount === 'coinbase'){
            // Crypto.com and Coinbase use similar structure
            orderParams = {
                quantity: amount,
                side: selectedBuySellButton,
                ordertype: fromAccount,
                cashqty: selectedButton === 'Dollars' ? amount : (fromAccount === 'LMT' ? strikePrice : undefined),
                qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR'
            };
        } else {
            // Default/IBKR structure (IBKR handled separately above)
            orderParams = {
                quantity: selectedButton === 'Shares' ? amount : "",
                side: selectedBuySellButton,
                cashqty: selectedButton === 'Dollars' ? amount : "",
                ordertype: fromAccount,
                qtyType: selectedButton === 'Shares' ? 'QUANTITY' : 'DOLLAR'
            };
        }
    
        // Validate orderParams exists and has required fields
        if (!orderParams || !orderParams.quantity) {
            showSweetAlert('Error!', 'Invalid order parameters. Please check your order details.', 'error');
            return;
        }
    
        try {
            const response = await createOrder(
                orderParams.quantity, 
                orderParams.side, 
                orderParams.ordertype, 
                orderParams.cashqty, 
                thirdParyAccount, 
                symbolData, 
                baseCurencySymbol, 
                curencySymbol,
                symbol_type,
                orderParams.qtyType,
                selectedCallPutButton,
                strikePrice,
                (thirdParyAccount === 'etrade' || thirdParyAccount === 'cryptocom') && fromAccount === 'LMT' ? timeInForce : undefined
            );
        
            console.log(response);
        
            if (response.details &&
                Array.isArray(response.details) &&
                response.details.length > 0) {
                // If details exist, check the order status
                // const orderStatus = response.details[0]?.order_status;
                // if (orderStatus === 'PreSubmitted' || orderStatus === 'Submitted') {
                //     setVerified(false);
                //     setAmount('');
                //     getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
                // } else {
                //     // If details exist but order status is unexpected
                //     showSweetAlert('Error!', `Unexpected order status: ${orderStatus}`, 'error');
                // }
                const detail = response.details[0];

                if (detail.message && detail.id) {
                    setConfirmationMessage(detail.message[0]);
                    setConfirmationId(detail.id);
                    setConfirmationRequired(true);
                    return;
                }

                // Handle success directly
                if ('order_id' in detail) {
                    setVerified(false);
                    setAmount('');
                    getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
                }
            } else if (response?.success === true) {
                // If details do not exist but success is true
                setVerified(false);
                setAmount('');
                getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
            } else if (response?.success === false) {
                // If success is explicitly false
                showSweetAlert('Error!', response?.error_response?.message || response?.error_response?.error_details || 'An error occurred', 'error');
            }else if(response?.PlaceOrderResponse){
                setVerified(false);
                setAmount('');
                getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
            }else if(response?.error){
                // Handle error response - could be from Crypto.com or other providers
                let errorMessage = 'An error occurred';
                
                // Check if error is a string (Crypto.com format: { error: "message", code: ... })
                if (typeof response.error === 'string') {
                    errorMessage = response.error;
                } else if (response.details) {
                    // Try to parse details if it exists (for other providers like E*Trade)
                    try {
                        const details = JSON.parse(response.details || '{}');
                        errorMessage = details?.Error?.message || errorMessage;
                    } catch (e) {
                        // If parsing fails, use error as-is
                        errorMessage = response.error || errorMessage;
                    }
                }
                
                showSweetAlert('Error!', errorMessage, 'error');
                return;
            }
             else if(thirdParyAccount !== 'etrade') {
                console.log(thirdParyAccount);
                // Catch any other unknown failure cases
                showSweetAlert('Error!', response?.error_details || "Something went wrong!", 'error');
            }
            
        } catch (error) {
            // Extract error message from API response if available
            let errorMessage = "Unexpected error occurred";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Map Crypto.com error codes to user-friendly messages
                const errorCodeMessages = {
                    306: "Insufficient funds or assets available for this order. Please check your account balance.",
                    10001: "Invalid request parameters.",
                    10002: "Invalid API key or secret.",
                    10003: "Invalid signature.",
                    10004: "Rate limit exceeded. Please try again later.",
                    10005: "Permission denied. Please check your API key permissions.",
                    10006: "Internal server error. Please try again later.",
                };
                
                // For Crypto.com, prioritize message field and map error codes
                if (errorData.code && errorCodeMessages[errorData.code]) {
                    errorMessage = errorCodeMessages[errorData.code];
                } else if (errorData.message && errorData.message !== errorData.error) {
                    // If message is different from error, use it
                    if (errorData.message.includes('_')) {
                        // Convert "INSUFFICIENT_AVAILABLE_BALANCE" to "Insufficient available balance"
                        errorMessage = errorData.message
                            .split('_')
                            .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                            .join(' ');
                    } else {
                        errorMessage = errorData.message;
                    }
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.error_details) {
                    errorMessage = errorData.error_details;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            showSweetAlert('Error!', errorMessage, 'error');
        }
        
    };

    const handlePreOrderConfirm = async (confirm) => {
        setPreOrderConfirmation(false);
        
        if (!confirm) {
            // User chose NO, go back to trade screen
            setOrderParams(null);
            return;
        }

        // User confirmed, proceed with order submission
        if (!orderParams) {
            return;
        }

        try {
            const response = await createOrder(
                orderParams.quantity, 
                orderParams.side, 
                orderParams.ordertype, 
                orderParams.cashqty, 
                thirdParyAccount, 
                symbolData, 
                baseCurencySymbol, 
                curencySymbol,
                symbol_type,
                undefined, // qtyType not used for IBKR
                selectedCallPutButton,
                strikePrice,
                undefined // timeInForce not used for IBKR
            );
        
            console.log(response);
        
            if (response.details &&
                Array.isArray(response.details) &&
                response.details.length > 0) {
                const detail = response.details[0];

                if (detail.message && detail.id) {
                    setConfirmationMessage(detail.message[0]);
                    setConfirmationId(detail.id);
                    setConfirmationRequired(true);
                    return;
                }

                // Handle success directly
                if ('order_id' in detail) {
                    setVerified(false);
                    setAmount('');
                    getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
                }
            } else if (response?.success === true) {
                // If details do not exist but success is true
                setVerified(false);
                setAmount('');
                getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
            } else if (response?.success === false) {
                // If success is explicitly false
                showSweetAlert('Error!', response?.error_response?.message || response?.error_response?.error_details || 'An error occurred', 'error');
            }else if(response?.PlaceOrderResponse){
                setVerified(false);
                setAmount('');
                getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
            }else if(response?.error){
                const details = JSON.parse(response?.details || '{}');
                showSweetAlert('Error!', details?.Error?.message || 'An error occurred', 'error');
            } else {
                console.log(thirdParyAccount);
                // Catch any other unknown failure cases
                showSweetAlert('Error!', response?.error_details || "Something went wrong!", 'error');
            }
            
        } catch (error) {
            showSweetAlert('Error!', error.message || "Unexpected error occurred", 'error');
        } finally {
            setOrderParams(null);
        }
    };

    const handleConfirmOrder = async (confirm) => {
        if (!confirm) {
            // User chose NO
            setConfirmationRequired(false);
            setConfirmationMessage('');
            setConfirmationId(null);
            setVerified(true); // Back to trade screen
            return;
        }

        try {
            const domain = localStorage.getItem('subDomain')
            const response = await axios.post(
                `https://${domain}.nodoji.com/custom-apis/confirmOrder`,
                { reply_id: confirmationId }
            );
            // const response = {
            // data: {
            //     details: [
            //     {
            //         order_id: "1234567890",
            //         order_status: "Submitted",
            //         encrypt_message: "1"
            //     }
            //     ]
            // }
            // };

            if (
                response.data.details &&
                Array.isArray(response.data.details) &&
                response.data.details.length > 0 &&
                'order_id' in response.data.details[0]
            ) {
                setVerified(false);
                setAmount('');
                getOrderData();
            } else {
                alert('Failed to confirm order.');
            }
        } catch (error) {
            console.error('Error confirming order:', error);
            alert('Error during order confirmation.');
        } finally {
            setConfirmationRequired(false);
            setConfirmationMessage('');
            setConfirmationId(null);
        }
    };

    const handleOrderSubmit = async (event) => {
        event.preventDefault(); 
        await getOrderData(thirdParyAccount, userData?.integrations?.trade_account_id);
        
        // Reset all form fields
        setAmount('');
        setStrikePrice('');
        setSelectedButton('Shares');
        setSelectedBuySellButton('BUY');
        setSelectedCallPutButton('CALL');
        setFromAccount('');
        setTimeInForce('Day');
        setVerified(true);
    }

    const handleDemo = () => {
        if(token){
            navigate('/pricing-plans', { state: { email: userData.email, name : userData.name } });
        }
        else{
            navigate('/signup');
        }
    }

    return (
        <>
            <Card className={`main-card mt-3 ${isDemoPath || userData?.added_integrations?.length === 0 ? 'blur-card' : ''}`}>
                {(isDemoPath || userData?.added_integrations?.length === 0) && (
                    <div className="blur-overlay">
                        <img
                            src={`${process.env.PUBLIC_URL}/images/lock-icon.svg`}
                            alt="Lock Icon"
                            style={{ height: '20px', width: '16px' }}
                            className="lock-icon"
                        />
                        {isDemoPath && <button className="subscribe-button" onClick={handleDemo}>Subscribe to access</button>}
                        {userData?.isPaymentDone && userData?.added_integrations?.length === 0 && (
                            <button className="subscribe-button" onClick={handleOpenSwitchAccount}>
                                Click to add Provider
                            </button>
                        )}
                    </div>
                )}
                <Card.Header className="pb-0">
                    <div className="d-flex justify-content-between mt-1">
                        <h2 className="medium-heading mb-0">Trade</h2>
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                onClick={handleSvgClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <path d="M12 13.5C11.7033 13.5 11.4133 13.412 11.1666 13.2472C10.92 13.0824 10.7277 12.8481 10.6142 12.574C10.5006 12.2999 10.4709 11.9983 10.5288 11.7074C10.5867 11.4164 10.7296 11.1491 10.9393 10.9393C11.1491 10.7296 11.4164 10.5867 11.7074 10.5288C11.9983 10.4709 12.2999 10.5006 12.574 10.6142C12.8481 10.7277 13.0824 10.92 13.2472 11.1666C13.412 11.4133 13.5 11.7033 13.5 12C13.5 12.3978 13.342 12.7794 13.0607 13.0607C12.7794 13.342 12.3978 13.5 12 13.5Z" fill="white" />
                                <path d="M18.75 13.5C18.4533 13.5 18.1633 13.412 17.9166 13.2472C17.67 13.0824 17.4777 12.8481 17.3642 12.574C17.2506 12.2999 17.2209 11.9983 17.2788 11.7074C17.3367 11.4164 17.4796 11.1491 17.6893 10.9393C17.8991 10.7296 18.1664 10.5867 18.4574 10.5288C18.7483 10.4709 19.0499 10.5006 19.324 10.6142C19.5981 10.7277 19.8324 10.92 19.9972 11.1666C20.162 11.4133 20.25 11.7033 20.25 12C20.25 12.3978 20.092 12.7794 19.8107 13.0607C19.5294 13.342 19.1478 13.5 18.75 13.5Z" fill="white" />
                                <path d="M5.25 13.5C4.95333 13.5 4.66332 13.412 4.41665 13.2472C4.16997 13.0824 3.97771 12.8481 3.86418 12.574C3.75065 12.2999 3.72094 11.9983 3.77882 11.7074C3.8367 11.4164 3.97956 11.1491 4.18934 10.9393C4.39912 10.7296 4.66639 10.5867 4.95737 10.5288C5.24834 10.4709 5.54994 10.5006 5.82403 10.6142C6.09812 10.7277 6.33238 10.92 6.49721 11.1666C6.66203 11.4133 6.75 11.7033 6.75 12C6.75 12.3978 6.59197 12.7794 6.31066 13.0607C6.02936 13.342 5.64783 13.5 5.25 13.5Z" fill="white" />
                            </svg>
                            <NavDropdown
                                show={showDropdown}
                                onToggle={handleToggle}
                                title=""
                                id="nav-dropdown"
                                align="end"
                                className="d-inline custom-nav-dropdown"
                                style={{ display: showDropdown ? 'block' : 'none' }}
                            >
                                <NavDropdown.Item className="dropdown-option" onClick={() => removeWidgetFromGrid('trade')}>Close Window</NavDropdown.Item>
                            </NavDropdown>
                        </div>
                    </div>
                    <div className="small-gray small-gary-date">{currentDate} | {currentTime}</div>
                </Card.Header>
                <Card.Body className="pt-0 pl-0 pr-0 scrollbar-custom">
                    {preOrderConfirmation ? (
                        <div className="text-center p-4">
                            <p style={{ whiteSpace: 'pre-line', fontSize: '16px' }}>
                                Are you sure you want to place this {orderParams?.side} order for {orderParams?.quantity || orderParams?.cashqty} {orderParams?.quantity ? 'shares' : 'dollars'}{orderParams?.ordertype ? ` at ${orderParams?.ordertype} price` : ''}?
                            </p>
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    onClick={() => handlePreOrderConfirm(true)}
                                    className="mx-2"
                                >
                                    Yes
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={() => handlePreOrderConfirm(false)}
                                    className="mx-2"
                                >
                                    No
                                </Button>
                            </div>
                        </div>
                    ) : confirmationRequired ? (
                        <div className="text-center p-4">
                            <p style={{ whiteSpace: 'pre-line', fontSize: '16px' }}>{confirmationMessage}</p>
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    onClick={() => handleConfirmOrder(true)}
                                    className="mx-2"
                                >
                                    Yes
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={() => handleConfirmOrder(false)}
                                    className="mx-2"
                                >
                                    No
                                </Button>
                            </div>
                        </div>
                    ) : verified ? (
                        <form>
                            <Row className="mb-2 control pl-1 pr-1">
                                <Col sm={6}>
                                    <Button
                                        variant="outlined"
                                        className={`ai-btn w-100 ${selectedBuySellButton === 'BUY' ? 'active' : ''}`}
                                        onClick={() => handleBuySellButtonClick('BUY')}
                                        style={{
                                            backgroundColor: selectedBuySellButton === 'BUY' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                            color: selectedBuySellButton === 'BUY' ? '#FFFFFF' : '#898889'
                                        }}
                                    >
                                        BUY
                                    </Button>
                                </Col>
                                <Col sm={6}>
                                    <Button
                                        variant="outlined"
                                        className={`ai-btn w-100 ${selectedBuySellButton === 'SELL' ? 'active' : ''}`}
                                        onClick={() => handleBuySellButtonClick('SELL')}
                                        style={{
                                            backgroundColor: selectedBuySellButton === 'SELL' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                            color: selectedBuySellButton === 'SELL' ? '#FFFFFF' : '#898889'
                                        }}
                                    >
                                        SELL
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mb-2 mt-2 control pl-1 pr-1">
                                <Col sm={6}>
                                    {/* Text Field for Amount */}
                                    <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            placeholder={selectedButton === "Shares" ? "Share Amount" : "Dollar Amount"}
                                            value={amount}
                                            onChange={handleAmountChange}
                                            fullWidth
                                            margin="normal"
                                            inputProps={{ className: 'no-arrows' }}
                                            type="number"
                                        />
                                    </FormControl>
                                </Col>
                                <Col sm={3}>
                                    <Button
                                        variant="outlined"
                                        className={`ai-btn w-100 ${selectedButton === 'Shares' ? 'active' : ''}`}
                                        onClick={() => handleButtonClick('Shares')}
                                        style={{
                                            backgroundColor: selectedButton === 'Shares' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                            color: selectedButton === 'Shares' ? '#FFFFFF' : '#898889'
                                        }}
                                    >
                                        Shares
                                    </Button>
                                </Col>
                                <Col sm={3}>
                                    <Button
                                        variant="outlined"
                                        className={`ai-btn w-100 ${selectedButton === 'Dollars' ? 'active' : ''}`}
                                        onClick={() => handleButtonClick('Dollars')}
                                        style={{
                                            backgroundColor: selectedButton === 'Dollars' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                            color: selectedButton === 'Dollars' ? '#FFFFFF' : '#898889'
                                        }}
                                    >
                                        Dollars
                                    </Button>
                                </Col>
                            </Row>

                            {symbol_type === 'otc' &&
                                <Row className="mb-2 control pl-1 pr-1">
                                    <Col sm={6}>
                                        <Button
                                            variant="outlined"
                                            className={`ai-btn w-100 ${selectedCallPutButton === 'CALL' ? 'active' : ''}`}
                                            onClick={() => handleCallPutButtonClick('CALL')}
                                            style={{
                                                backgroundColor: selectedCallPutButton === 'CALL' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                                color: selectedCallPutButton === 'CALL' ? '#FFFFFF' : '#898889'
                                            }}
                                        >
                                            CALL
                                        </Button>
                                    </Col>
                                    <Col sm={6}>
                                        <Button
                                            variant="outlined"
                                            className={`ai-btn w-100 ${selectedCallPutButton === 'PUT' ? 'active' : ''}`}
                                            onClick={() => handleCallPutButtonClick('PUT')}
                                            style={{
                                                backgroundColor: selectedCallPutButton === 'PUT' ? 'rgba(49, 124, 255, 0.20)' : 'transparent',
                                                color: selectedCallPutButton === 'PUT' ? '#FFFFFF' : '#898889'
                                            }}
                                        >
                                            PUT
                                        </Button>
                                    </Col>
                                </Row>
                            }

                            {symbol_type === 'otc' ? (
                                <Row className="mb-2 mt-2 control pl-1 pr-1">
                                    <Col sm={6}>
                                        {/* Text Field for Amount */}
                                        <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                            <TextField
                                                placeholder="Strike Price"
                                                value={strikePrice}
                                                onChange={handleStrikePriceChange}
                                                fullWidth
                                                margin="normal"
                                                inputProps={{ className: 'no-arrows' }}
                                                type="number"
                                            />
                                        </FormControl>
                                    </Col>
                                    <Col sm={6}>
                                        <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                            <Select
                                                value={fromAccount}
                                                onChange={handleFromAccountChange}
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                className="square-select"
                                            >
                                                <MenuItem value=""> Order Type </MenuItem>
                                                <MenuItem value="MKT"> Market </MenuItem>
                                                <MenuItem value="LMT"> Limit </MenuItem>
                                                {/* <MenuItem value="TRAILLMT"> Trail Limit </MenuItem> */}
                                            </Select>
                                        </FormControl>
                                    </Col>
                                </Row>
                            ) : fromAccount === 'MKT' || fromAccount === '' ? (
                                <Row className="mb-2 pl-1 pr-1">
                                    <Col sm={12}>
                                        <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                            <Select
                                                value={fromAccount}
                                                onChange={handleFromAccountChange}
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                className="square-select"
                                            >
                                                <MenuItem value=""> Order Type </MenuItem>
                                                <MenuItem value="MKT"> Market </MenuItem>
                                                <MenuItem value="LMT"> Limit </MenuItem>
                                                {/* <MenuItem value="TRAILLMT"> Trail Limit </MenuItem> */}
                                            </Select>
                                        </FormControl>
                                    </Col>
                                </Row>
                            ) : 
                                <>
                                    <Row className="mb-2 mt-2 control pl-1 pr-1">
                                        <Col sm={6}>
                                            <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                                <Select
                                                    value={fromAccount}
                                                    onChange={handleFromAccountChange}
                                                    displayEmpty
                                                    inputProps={{ 'aria-label': 'Without label' }}
                                                    className="square-select"
                                                >
                                                    <MenuItem value=""> Order Type </MenuItem>
                                                    <MenuItem value="MKT"> Market </MenuItem>
                                                    <MenuItem value="LMT"> Limit </MenuItem>
                                                    {/* <MenuItem value="TRAILLMT"> Trail Limit </MenuItem> */}
                                                </Select>
                                            </FormControl>
                                        </Col>
                                        <Col sm={6}>
                                            {/* Text Field for Amount */}
                                            <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                                <TextField
                                                    placeholder="Limit Price"
                                                    value={strikePrice}
                                                    onChange={handleStrikePriceChange}
                                                    fullWidth
                                                    margin="normal"
                                                    inputProps={{ className: 'no-arrows' }}
                                                    type="number"
                                                />
                                            </FormControl>
                                        </Col>
                                    </Row>

                                    <Row className="mb-2 pl-1 pr-1">
                                    <Col sm={12}>
                                        <FormControl className="square-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                                            <Select
                                                value={timeInForce}
                                                onChange={handleTimeInForceChange}
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                className="square-select"
                                            >
                                                {/* <MenuItem value=""> Time in force </MenuItem> */}
                                                <MenuItem value="Day"> Day Order </MenuItem>
                                                <MenuItem value="Good"> Good Till Cancelled </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Col>
                                </Row>
                                </>
                            }

                            <div className="col-12 d-flex justify-content-between mt-3 d-none">
                                <div><p className="gray-text mb-2">Total(est.)</p></div>
                                <div><p className="mb-2">$8.56</p></div>
                            </div>
                            {thirdParyAccount === 'IBKR' && (
                                <div className="row pl-1 pr-1">
                                    <div className="col-4"></div>
                                    <div className="col-4">
                                        <Button variant="contained" disabled={isSubmitDisabled} size="small" className="btn-primary multi-b-s w-100 text-capitalize" 
                                        style={{
                                            color: isSubmitDisabled ? '#000000' : '#FFFFFF', // White text when disabled
                                            backgroundColor: isSubmitDisabled ? '#D3D3D3' : '#2979FF', // Disabled shade or primary color
                                            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer', // Change cursor style
                                            opacity: isSubmitDisabled ? 0.6 : 1 // Adjust opacity for disabled state
                                        }} onClick={handleSubmit}>Submit</Button>
                                    </div>
                                    <div className="col-5 d-none">
                                        <Button variant="outlined" size="small" className="outlined-btn w-100">Preview</Button>
                                    </div>
                                </div>
                            )}
                            {thirdParyAccount != 'IBKR' && (
                            <div className="row pl-1 pr-1 justify-content-center">
                            {/* <div className="col-1"></div>
                            <div className="col-5">
                                <Button variant="contained" disabled={isSubmitDisabled} size="small" className="btn-primary multi-b-s w-100 text-capitalize" 
                                style={{
                                    color: isSubmitDisabled ? '#000000' : '#FFFFFF', // White text when disabled
                                    backgroundColor: isSubmitDisabled ? '#D3D3D3' : '#2979FF', // Disabled shade or primary color
                                    cursor: isSubmitDisabled ? 'not-allowed' : 'pointer', // Change cursor style
                                    opacity: isSubmitDisabled ? 0.6 : 1 // Adjust opacity for disabled state
                                }} onClick={handleSubmit}>Submit</Button>
                            </div>
                            <div className="col-5">
                                <Button variant="outlined" size="small" className="w-100" disabled={isSubmitDisabled} onClick={handleOpenPreview}
                                style={{
                                    borderRadius: '4px',
                                    color: '#898889',
                                    textTransform: 'none',
                                    border: isSubmitDisabled ? '1px solid #48494C' : '1px solid #2979FF',
                                }}>
                                    Preview
                                </Button>
                            </div> */}
                            {/* <div className="mb-1 row "> */}
                                <div 
                                    style={{ position: 'relative', width: '100%' }}
                                    onMouseEnter={() => isSymbolInvalid && setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                >
                                    <Button 
                                        variant="primary" 
                                        style={{ 
                                            backgroundColor: isSymbolInvalid ? '#48494C' : '#317CFF', 
                                            textTransform: 'none',
                                            cursor: isSymbolInvalid ? 'not-allowed' : 'pointer',
                                            opacity: isSymbolInvalid ? 0.6 : 1,
                                            width: '100%'
                                        }} 
                                        onClick={isSymbolInvalid ? undefined : handleOpenPreview} 
                                        disabled={isSymbolInvalid}
                                        className="w-65 p-2 btn-primary"
                                    >
                                        Preview Order
                                    </Button>
                                    {isSymbolInvalid && showTooltip && (
                                        <div 
                                            style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginBottom: '5px',
                                                padding: '10px 14px',
                                                backgroundColor: '#2a2a2a',
                                                color: '#ffffff',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                                zIndex: 1000,
                                                pointerEvents: 'none',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                maxWidth: '280px',
                                                textAlign: 'center',
                                                lineHeight: '1.4'
                                            }}
                                        >
                                            Selected symbol in chart is not supported by Crypto.com Exchange
                                            <div 
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    border: '6px solid transparent',
                                                    borderTopColor: '#2a2a2a'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            {/* </div> */}
                            </div>
                            )}
                        </form>
                    ) : (
                        <>
                            <Row className="text-center">
                                <Col sm={12}>
                                    <img 
                                    style={{ height: "100px", width: "100px"}} 
                                    src={`${process.env.PUBLIC_URL}/images/green_check.svg`} 
                                    alt="green_check" 
                                    className="mb-2" 
                                    />
                                </Col>
                                <Col sm={12}>
                                    <p style={{ fontSize: '18px', margin: '6px 0' }}>Submit successfully!</p>
                                </Col>
                            </Row>
                            <Form onSubmit={handleOrderSubmit}>
                            <div className="mb-1 row justify-content-center">
                                <Button variant="primary" type="submit" style={{ backgroundColor: '#317CFF', textTransform: 'none' }} className="mt-4 w-65 p-2 btn-primary">
                                    Finish
                                </Button>
                            </div>
                            </Form>
                        </>
                    )}
                </Card.Body>
            </Card>

            <SwitchAccount open={switchAccount} onClose={handleCloseSwitchAccount}/>
            <PreviewOrderPopup open={showPreview} onClose={handleClosePreview} orderParams={orderParams} thirdParyAccount={thirdParyAccount} symbol_type={symbol_type} setConfirmationMessage={setConfirmationMessage} setConfirmationId={setConfirmationId} setConfirmationRequired={setConfirmationRequired} setVerified={setVerified} setAmount={setAmount}/>
        </>
    );
}

export default Trade;